'use client';

import React, { useEffect, useRef, useCallback, useState } from 'react';
import Image from 'next/image';
import { cleanAudioUrl, extractDomain } from '@/lib/audio-proxy-utils';
import { mediaCueRegistry } from '@/lib/media-cue-registry';

interface VideoPlayerProps {
  url: string;
  className?: string;
  isPlaying?: boolean;
  currentTime?: number; // 新增：外部时间同步
  thumbnailUrl?: string;
  onPlayPause?: (isPlaying: boolean) => void;
  onTimeUpdate?: (currentTime: number) => void;
  onDurationChange?: (duration: number) => void;
  onSeekTo?: (player: {
    seekTo: (time: number) => void;
    setPlaybackRate: (speed: number) => void;
    getCurrentTime: () => number;
    play: () => void;
    pause: () => void;
  }) => void;
  onEnded?: () => void;
  // Media Session API props
  title?: string;
  artist?: string;
  album?: string;
  artwork?: Array<{
    src: string;
    sizes: string;
    type: string;
  }>;
  muted?: boolean; // 新增：控制视频是否静音
  enableTapToggle?: boolean;
  maintainAspectRatio?: boolean;
}

export function VideoPlayer({
  url,
  className,
  isPlaying,
  currentTime, // 新增：外部时间同步
  thumbnailUrl,
  onPlayPause,
  onTimeUpdate,
  onDurationChange,
  onSeekTo,
  onEnded,
  title,
  artist,
  album,
  artwork,
  muted = false, // 默认不静音
  enableTapToggle = false,
  maintainAspectRatio = true
}: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isSeeking, setIsSeeking] = useState(false);
  const playStateChangedInternally = useRef(false);
  const [hasStartedPlaying, setHasStartedPlaying] = useState(false);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const externalPlayStateChange = useRef(false);
  const seekThenPlayRequestTime = useRef<number>(0);
  const lastPlayRequestTime = useRef<number>(0);
  const hasAppliedInitialTime = useRef(false); // 🔥 新增：跟踪是否已应用初始时间
  const mediaMetadataInitRef = useRef<MediaMetadataInit | null>(null);
  const [aspectRatio, setAspectRatio] = useState(16 / 9);
  const isPortrait = aspectRatio < 1;
  const seekDebounceRef = useRef<NodeJS.Timeout | null>(null);

  const setPositionStateSafe = useCallback((overrides?: {
    duration?: number;
    position?: number;
    playbackRate?: number;
  }) => {
    if (typeof navigator === "undefined") {
      return;
    }

    const mediaSession = navigator.mediaSession as MediaSession | undefined;
    const setPositionState = mediaSession?.setPositionState;
    if (typeof setPositionState !== "function") {
      return;
    }

    const video = videoRef.current;
    const rawDuration = overrides?.duration ?? video?.duration;
    const rawPosition = overrides?.position ?? video?.currentTime;
    const rawRate = overrides?.playbackRate ?? video?.playbackRate ?? 1;

    if (typeof rawDuration !== "number" || !Number.isFinite(rawDuration) || rawDuration < 0) {
      return;
    }

    if (typeof rawPosition !== "number" || !Number.isFinite(rawPosition)) {
      return;
    }

    const safeDuration = Math.max(0, rawDuration);
    const safePosition = Math.min(Math.max(rawPosition, 0), safeDuration);
    const safeRate = typeof rawRate === "number" && Number.isFinite(rawRate) ? rawRate : 1;

    try {
      setPositionState.call(mediaSession, {
        duration: safeDuration,
        playbackRate: safeRate,
        position: safePosition,
      });
    } catch (error) {
      console.error("Error setting media session position state:", error);
    }
  }, []);

  const allowCustomMediaSessionActions = React.useMemo(() => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return true;
    }

    const ua = navigator.userAgent || '';
    const isIOS = /iP(hone|od|ad)/i.test(ua);

    if (!isIOS) {
      return true;
    }

    const isStandaloneDisplay = window.matchMedia('(display-mode: standalone)').matches;
    const isNavigatorStandalone = Boolean((navigator as any).standalone);

    // iOS standalone PWA: rely on native handlers so lock screen controls keep working when suspended.
    return !(isStandaloneDisplay || isNavigatorStandalone);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    const inlineAttributes: Array<[string, string]> = [
      ['playsinline', 'true'],
      ['webkit-playsinline', 'true'],
      ['x5-playsinline', 'true'],
      ['data-playsinline', 'true'],
      ['x-webkit-airplay', 'deny'],
      ['controlslist', 'nodownload nofullscreen noremoteplayback'],
    ];

    try {
      video.playsInline = true;
      video.disableRemotePlayback = true;
    } catch {
      // ignore unsupported properties
    }

    inlineAttributes.forEach(([key, value]) => {
      try {
        video.setAttribute(key, value);
      } catch {
        // ignore attribute set failures
      }
    });
  }, []);

  // 响应外部isPlaying属性的变化
  useEffect(() => {
    if (!videoRef.current) return;
    
    // 🔥 修复：静音时仍然播放视频画面，但确保音频静音
    if (muted) {
      // 确保视频静音但仍能播放画面
      videoRef.current.muted = true;
      videoRef.current.volume = 0;
      // console.log('🔇 Video is muted but will play visually');
    }
    
    // 标记这是由外部prop引起的变化，避免循环
    externalPlayStateChange.current = true;
    
    try {
      // 检查是否为YouTube URL，如果是就不播放HTML5 video
      const isYouTubeUrl = url && (url.includes('youtube.com') || url.includes('youtu.be'));
      
      if (isPlaying && !isYouTubeUrl) {
        // console.log('🎵 VideoPlayer attempting to play');
        const playPromise = videoRef.current.play();
        if (playPromise !== undefined) {
          playPromise.then(() => {
            console.log('🎵 VideoPlayer play success');
            setHasStartedPlaying(true);
            lastPlayRequestTime.current = Date.now();
          }).catch(error => {
            if ((error as DOMException)?.name === 'AbortError') {
              console.warn('Video play interrupted by pause request');
            } else {
              console.error('Video play failed:', error);
            }
          }).finally(() => {
            // 重置标记
            setTimeout(() => {
              externalPlayStateChange.current = false;
            }, 100);
          });
        }
      } else if (!isYouTubeUrl) {
        videoRef.current.pause();
      }
      
      // 重置标记
      setTimeout(() => {
          externalPlayStateChange.current = false;
        }, 100);
    } catch (error) {
      console.error('Error controlling video:', error);
      externalPlayStateChange.current = false;
    }
  }, [isPlaying, muted]);

  // 重置播放状态当URL变化时
  useEffect(() => {
    setHasStartedPlaying(false);
    setIsVideoLoaded(false);
    hasAppliedInitialTime.current = false; // 🔥 新增：重置初始时间应用标志
    setAspectRatio(16 / 9);
    
    // 🔥 确保静音设置在URL变化时也生效
    if (videoRef.current && muted) {
      videoRef.current.muted = true;
      videoRef.current.volume = 0;
      // console.log('🔇 Video muted on URL change');
    }
  }, [url, muted]);

  // Media Session API 设置
  useEffect(() => {
    // 检查协议和浏览器支持
    const isHTTPS = window.location.protocol === 'https:' || window.location.hostname === 'localhost';
    const hasMediaSession = 'mediaSession' in navigator;
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);

    const setActionHandlerSafely = (
      action: MediaSessionAction,
      handler: MediaSessionActionHandler | null
    ) => {
      if (!allowCustomMediaSessionActions || !hasMediaSession) {
        return;
      }

      try {
        navigator.mediaSession!.setActionHandler(action, handler);
      } catch (error) {
        console.error('Media Session action handler error:', error);
      }
    };

    if (!isHTTPS || !hasMediaSession || !title) {
      return;
    }

    try {
      // Safari需要特殊的图片处理
      let artworkArray: Array<{
        src: string;
        sizes: string;
        type: string;
      }> = [];
      
      if (thumbnailUrl) {
        if (isSafari) {
          // Safari更喜欢JPEG格式和特定尺寸
          artworkArray = [
            {
              src: thumbnailUrl,
              sizes: '96x96',
              type: 'image/jpeg'
            },
            {
              src: thumbnailUrl,
              sizes: '128x128',
              type: 'image/jpeg'
            },
            {
              src: thumbnailUrl,
              sizes: '192x192',
              type: 'image/jpeg'
            },
            {
              src: thumbnailUrl,
              sizes: '256x256',
              type: 'image/jpeg'
            },
            {
              src: thumbnailUrl,
              sizes: '384x384',
              type: 'image/jpeg'
            },
            {
              src: thumbnailUrl,
              sizes: '512x512',
              type: 'image/jpeg'
            }
          ];
        } else {
          // Chrome使用PNG格式
          artworkArray = [
            {
              src: thumbnailUrl,
              sizes: '96x96',
              type: 'image/png'
            },
            {
              src: thumbnailUrl,
              sizes: '128x128', 
              type: 'image/png'
            },
            {
              src: thumbnailUrl,
              sizes: '192x192',
              type: 'image/png'
            },
            {
              src: thumbnailUrl,
              sizes: '256x256',
              type: 'image/png'
            },
            {
              src: thumbnailUrl,
              sizes: '384x384',
              type: 'image/png'
            },
            {
              src: thumbnailUrl,
              sizes: '512x512',
              type: 'image/png'
            }
          ];
        }
      }

      // 设置媒体元数据
      const metadataInit: MediaMetadataInit = {
        title: title || 'Unknown Title',
        artist: artist || 'Unknown Artist',
        album: album || 'Podcast Episode',
        artwork: artwork || artworkArray
      };

      mediaMetadataInitRef.current = metadataInit;

      if ('MediaMetadata' in window) {
        navigator.mediaSession!.metadata = new (window as any).MediaMetadata(metadataInit);
      }
      const setupActionHandlers = () => {
        if (!allowCustomMediaSessionActions) {
          return;
        }

        setActionHandlerSafely('play', () => {
          if (videoRef.current) {
            const isYouTubeUrl = url && (url.includes('youtube.com') || url.includes('youtu.be'));
            if (isYouTubeUrl) {
              console.log('🎬 Media Session: Skipping HTML5 video play for YouTube URL');
              return;
            }

            if (muted) {
              videoRef.current.muted = true;
              videoRef.current.volume = 0;
              console.log('🔇 Media Session play: Video is muted but will play visually');
            }
            videoRef.current.play();
            if (onPlayPause && !isPlaying) {
              onPlayPause(true);
            }
          }
        });

        setActionHandlerSafely('pause', () => {
          if (videoRef.current) {
            if (muted) {
              console.log('🔇 Media Session pause: Video is muted but will pause visually');
            }
            videoRef.current.pause();
            if (onPlayPause && isPlaying) {
              onPlayPause(false);
            }
          }
        });

        setActionHandlerSafely('seekbackward', (details) => {
          if (videoRef.current) {
            const skipTime = details.seekOffset || 10;
            videoRef.current.currentTime = Math.max(videoRef.current.currentTime - skipTime, 0);
          }
        });

        setActionHandlerSafely('seekforward', (details) => {
          if (videoRef.current) {
            const skipTime = details.seekOffset || 10;
            videoRef.current.currentTime = Math.min(
              videoRef.current.currentTime + skipTime,
              videoRef.current.duration
            );
          }
        });

        setActionHandlerSafely('seekto', (details) => {
          if (videoRef.current && details.seekTime) {
            videoRef.current.currentTime = details.seekTime;
          }
        });
      };

      if (allowCustomMediaSessionActions) {
        if (isSafari) {
          setTimeout(setupActionHandlers, 100);
        } else {
          setupActionHandlers();
        }
      }

      // 设置播放状态
      navigator.mediaSession!.playbackState = isPlaying ? 'playing' : 'paused';

    } catch (error) {
      console.error('Error setting up Media Session API:', error);
    }

    return () => {
      // 清理时重置媒体会话
      if (hasMediaSession) {
        try {
          if (allowCustomMediaSessionActions) {
            navigator.mediaSession!.metadata = null;
            setActionHandlerSafely('play', null);
            setActionHandlerSafely('pause', null);
            setActionHandlerSafely('seekbackward', null);
            setActionHandlerSafely('seekforward', null);
            setActionHandlerSafely('seekto', null);
          }
        } catch {
          // Silently handle cleanup errors
        }
      }
    };
  }, [title, artist, album, artwork, thumbnailUrl, isPlaying, onPlayPause, allowCustomMediaSessionActions, muted, url, setPositionStateSafe]);

  // 恢复媒体会话数据，防止 iOS PWA 在后台暂停后丢失连接
  useEffect(() => {
    if (typeof document === 'undefined' || typeof navigator === 'undefined') {
      return;
    }

    if (!('mediaSession' in navigator)) {
      return;
    }

    const restoreMediaSession = () => {
      if (!mediaMetadataInitRef.current) {
        return;
      }

      try {
        if ('MediaMetadata' in window) {
          navigator.mediaSession!.metadata = new (window as any).MediaMetadata(mediaMetadataInitRef.current);
        }

        if (videoRef.current) {
          const video = videoRef.current;
          navigator.mediaSession!.playbackState = video.paused ? 'paused' : 'playing';
          setPositionStateSafe({
            duration: video.duration,
            playbackRate: video.playbackRate || 1,
            position: video.currentTime,
          });
        }
      } catch (error) {
        console.error('Error restoring media session:', error);
      }
    };

    document.addEventListener('visibilitychange', restoreMediaSession);
    window.addEventListener('pageshow', restoreMediaSession);
    window.addEventListener('focus', restoreMediaSession);

    return () => {
      document.removeEventListener('visibilitychange', restoreMediaSession);
      window.removeEventListener('pageshow', restoreMediaSession);
      window.removeEventListener('focus', restoreMediaSession);
    };
  }, []);

  // 更新媒体会话的播放状态
  useEffect(() => {
    if ('mediaSession' in navigator) {
      navigator.mediaSession!.playbackState = isPlaying ? 'playing' : 'paused';
    }
  }, [isPlaying]);

  // 🔥 修复：一次性应用缓存时间，然后正常同步
  useEffect(() => {
    if (!videoRef.current || currentTime === undefined) return;
    
    const videoCurrentTime = videoRef.current.currentTime;
    const timeDifference = Math.abs(videoCurrentTime - currentTime);
    
    if (!hasAppliedInitialTime.current && currentTime > 0 && videoCurrentTime === 0 && isVideoLoaded) {
      setIsSeeking(true);
      if (seekDebounceRef.current) {
        clearTimeout(seekDebounceRef.current);
      }
      videoRef.current.currentTime = currentTime;
      hasAppliedInitialTime.current = true;
      seekDebounceRef.current = setTimeout(() => setIsSeeking(false), 200);
      return;
    }
    
    if (timeDifference > 0.1) {
      setIsSeeking(true);
      if (seekDebounceRef.current) {
        clearTimeout(seekDebounceRef.current);
      }
      videoRef.current.currentTime = currentTime;
      seekDebounceRef.current = setTimeout(() => setIsSeeking(false), 200);
    }
  }, [currentTime, isVideoLoaded]);

  useEffect(() => {
    return () => {
      if (seekDebounceRef.current) {
        clearTimeout(seekDebounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    const handleEnded = () => {
      try {
        video.pause();
      } catch {
        // ignore
      }
      setPositionStateSafe({
        duration: video.duration,
        playbackRate: video.playbackRate || 1,
        position: video.duration || video.currentTime || 0,
      });
      if (onTimeUpdate) {
        onTimeUpdate(video.duration || video.currentTime || 0);
      }
      if (onPlayPause && !video.loop) {
        onPlayPause(false);
      }
      if (onEnded) {
        onEnded();
      }
    };

    video.addEventListener('ended', handleEnded);
    return () => {
      video.removeEventListener('ended', handleEnded);
    };
  }, [onTimeUpdate, onPlayPause, onEnded, setPositionStateSafe]);

  // 🔥 修复：强制保持静音状态，防止被其他代码覆盖
  useEffect(() => {
    if (!videoRef.current || !muted) return;
    
    // 定期检查并强制保持静音状态
    const enforceQuietMode = () => {
      if (videoRef.current && muted) {
        if (!videoRef.current.muted || videoRef.current.volume > 0) {
          console.log('🔇 Enforcing quiet mode - video was unmuted, fixing...');
          videoRef.current.muted = true;
          videoRef.current.volume = 0;
        }
      }
    };
    
    // 立即执行一次
    enforceQuietMode();
    
    // 每秒检查一次，确保始终保持静音
    const interval = setInterval(enforceQuietMode, 1000);
    
    return () => clearInterval(interval);
  }, [muted]);

  // 更新媒体会话的位置状态
  useEffect(() => {
    if ('mediaSession' in navigator && videoRef.current) {
      const updatePositionState = () => {
        if (videoRef.current && videoRef.current.duration && !isNaN(videoRef.current.duration)) {
          setPositionStateSafe({
            duration: videoRef.current.duration,
            playbackRate: videoRef.current.playbackRate,
            position: videoRef.current.currentTime,
          });
        }
      };

      const video = videoRef.current;
      video.addEventListener('timeupdate', updatePositionState);
      video.addEventListener('durationchange', updatePositionState);

      return () => {
        video.removeEventListener('timeupdate', updatePositionState);
        video.removeEventListener('durationchange', updatePositionState);
      };
    }
  }, [setPositionStateSafe]);

  // 设置onSeekTo回调函数
  useEffect(() => {
    if (videoRef.current && onSeekTo) {
      const playerAPI = {
        seekTo: (time: number) => {
          if (videoRef.current && !isSeeking) {
            setIsSeeking(true);
            videoRef.current.currentTime = time;
            // 记录这是一个seek操作的时间戳，用于后续判断
            seekThenPlayRequestTime.current = Date.now();
            setTimeout(() => setIsSeeking(false), 100);
          }
        },
        setPlaybackRate: (speed: number) => {
          if (videoRef.current) {
            videoRef.current.playbackRate = speed;
          }
        },
        getCurrentTime: () => {
          return videoRef.current ? videoRef.current.currentTime : 0;
        },
        play: () => {
          if (videoRef.current) {
            // 检查是否为YouTube URL，如果是就不播放HTML5 video
            const isYouTubeUrl = url && (url.includes('youtube.com') || url.includes('youtu.be'));
            if (isYouTubeUrl) {
              console.log('🎬 PlayerAPI: Skipping HTML5 video play for YouTube URL');
              return;
            }
            
            // 🔥 修复：静音时仍然播放视频画面，但确保音频静音
            if (muted) {
              videoRef.current.muted = true;
              videoRef.current.volume = 0;
              console.log('🔇 PlayerAPI play: Video is muted but will play visually');
            }
        
            playStateChangedInternally.current = true;
            // 记录外部播放请求的时间
            lastPlayRequestTime.current = Date.now();
            
            const playPromise = videoRef.current.play();
            if (playPromise !== undefined) {
              playPromise.then(() => {
                setHasStartedPlaying(true);
                if (onPlayPause && !isPlaying) {
                  onPlayPause(true);
                }
                setTimeout(() => {
                  playStateChangedInternally.current = false;
                }, 100);
              }).catch(error => {
                if ((error as DOMException)?.name === 'AbortError') {
                  console.warn('Video play interrupted by pause request');
                } else {
                  console.error('Video play failed:', error);
                }
                playStateChangedInternally.current = false;
              });
            }
          }
        },
        pause: () => {
          if (videoRef.current) {
            // 🔥 修复：静音时仍然需要暂停视频画面
            if (muted) {
              // console.log('🔇 PlayerAPI pause: Video is muted but will pause visually');
            }
            
            playStateChangedInternally.current = true;
            videoRef.current.pause();
            if (onPlayPause && isPlaying) {
              onPlayPause(false);
            }
            setTimeout(() => {
              playStateChangedInternally.current = false;
            }, 100);
          }
        }
      };
      onSeekTo(playerAPI);
    }
  }, [onSeekTo, isPlaying, onPlayPause, isSeeking]);

  // 处理时间更新
  const handleTimeUpdate = useCallback(() => {
    if (videoRef.current && onTimeUpdate) {
      // console.log('🕐 VideoPlayer time update:', videoRef.current.currentTime);
      onTimeUpdate(videoRef.current.currentTime);
    }
  }, [onTimeUpdate]);

  // 处理视频时长变化
  const handleDurationChange = useCallback(() => {
    if (videoRef.current && onDurationChange) {
      const duration = videoRef.current.duration;
      if (!isNaN(duration) && duration > 0) {
        console.log('🎞️ handleDurationChange captured duration', {
          duration,
          src: videoRef.current?.currentSrc || videoRef.current?.src,
        });
        onDurationChange(duration);
      }
    }
  }, [onDurationChange]);

  // 处理元数据加载完成
  const handleLoadedMetadata = useCallback(() => {
    if (!videoRef.current) {
      return;
    }

    const videoWidth = videoRef.current.videoWidth || 0;
    const videoHeight = videoRef.current.videoHeight || 0;

    if (videoWidth > 0 && videoHeight > 0) {
      const ratio = videoWidth / videoHeight;
      setAspectRatio(ratio);
    }

    if (onDurationChange) {
      const duration = videoRef.current.duration;
      if (!isNaN(duration) && duration > 0) {
        console.log('🧭 handleLoadedMetadata duration resolved', {
          duration,
          readyState: videoRef.current.readyState,
          src: videoRef.current?.currentSrc || videoRef.current?.src,
        });
        onDurationChange(duration);
        setIsVideoLoaded(true);
      }
    }
  }, [onDurationChange]);

  // 处理缩略图点击
  const handleThumbnailClick = useCallback(() => {
    if (!hasStartedPlaying && onPlayPause) {
      onPlayPause(true); // 开始播放
    }
  }, [hasStartedPlaying, onPlayPause]);


  // 处理原生视频事件，同步到外部状态
  const handleNativePlay = useCallback(() => {
    const now = Date.now();
    const timeSinceSeekRequest = now - seekThenPlayRequestTime.current;
    
    // 强制初始化 Media Session API（在用户交互后）
    if ('mediaSession' in navigator && title) {
      const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      
      try {
        // Safari特殊处理：强制重新设置整个Media Session
        if (isSafari) {
          // 移动端Safari需要特殊处理
          if (isMobile) {
            // 🔥 修复：确保视频元素在episode页面保持静音
            if (videoRef.current) {
              if (muted) {
                videoRef.current.muted = true;
                videoRef.current.volume = 0;
                console.log('🔇 Mobile Safari: Video kept muted for episode page');
              } else {
                videoRef.current.muted = false;
                videoRef.current.volume = 0.8;
              }
              
              // 强制设置media session在移动端
              setTimeout(() => {
                navigator.mediaSession!.playbackState = 'playing';
                
                // 移动端需要重新设置metadata
                if ('MediaMetadata' in window) {
                  navigator.mediaSession!.metadata = new (window as any).MediaMetadata({
                    title: title,
                    artist: artist || 'Unknown Artist',
                    album: album || 'Podcast Episode',
                    artwork: thumbnailUrl ? [
                      {
                        src: thumbnailUrl,
                        sizes: '512x512',
                        type: 'image/jpeg'
                      }
                    ] : []
                  });
                }
                
                // 移动端Safari需要设置position state
                if (videoRef.current) {
                  setPositionStateSafe({
                    duration: videoRef.current.duration,
                    playbackRate: videoRef.current.playbackRate || 1,
                    position: videoRef.current.currentTime || 0,
                  });
                }
                
              }, 100);
            }
          } else {
            // 桌面Safari处理
            navigator.mediaSession!.playbackState = 'playing';
            
            if ('MediaMetadata' in window) {
              const safariMetadata = new (window as any).MediaMetadata({
                title: title,
                artist: artist || 'Unknown Artist',
                album: album || 'Podcast Episode',
                artwork: thumbnailUrl ? [
                  {
                    src: thumbnailUrl,
                    sizes: '96x96',
                    type: 'image/jpeg'
                  },
                  {
                    src: thumbnailUrl,
                    sizes: '128x128',
                    type: 'image/jpeg'
                  },
                  {
                    src: thumbnailUrl,
                    sizes: '192x192',
                    type: 'image/jpeg'
                  },
                  {
                    src: thumbnailUrl,
                    sizes: '256x256',
                    type: 'image/jpeg'
                  },
                  {
                    src: thumbnailUrl,
                    sizes: '384x384',
                    type: 'image/jpeg'
                  },
                  {
                    src: thumbnailUrl,
                    sizes: '512x512',
                    type: 'image/jpeg'
                  }
                ] : []
              });
              
              navigator.mediaSession!.metadata = safariMetadata;
            }
            
            if (allowCustomMediaSessionActions) {
              navigator.mediaSession!.setActionHandler('play', () => {
                if (videoRef.current) {
                  // 检查是否为YouTube URL，如果是就不播放HTML5 video
                  const isYouTubeUrl = url && (url.includes('youtube.com') || url.includes('youtu.be'));
                  if (isYouTubeUrl) {
                    console.log('🎬 Safari Media Session: Skipping HTML5 video play for YouTube URL');
                    return;
                  }
                  
                  if (muted) {
                    videoRef.current.muted = true;
                    videoRef.current.volume = 0;
                    console.log('🔇 Safari Media Session play: Video is muted but will play visually');
                  }
                  videoRef.current.play();
                }
              });

              navigator.mediaSession!.setActionHandler('pause', () => {
                if (videoRef.current) {
                  if (muted) {
                    console.log('🔇 Safari Media Session pause: Video is muted but will pause visually');
                  }
                  videoRef.current.pause();
                }
              });

              navigator.mediaSession!.setActionHandler('seekbackward', (details) => {
                if (videoRef.current) {
                  const skipTime = details.seekOffset || 10;
                  videoRef.current.currentTime = Math.max(videoRef.current.currentTime - skipTime, 0);
                }
              });

              navigator.mediaSession!.setActionHandler('seekforward', (details) => {
                if (videoRef.current) {
                  const skipTime = details.seekOffset || 10;
                  videoRef.current.currentTime = Math.min(
                    videoRef.current.currentTime + skipTime,
                    videoRef.current.duration
                  );
                }
              });
            }
            
            // 桌面Safari设置position state
            if (videoRef.current) {
              setPositionStateSafe({
                duration: videoRef.current.duration,
                playbackRate: videoRef.current.playbackRate,
                position: videoRef.current.currentTime,
              });
            }
          }
          
        } else {
          // Chrome标准处理
          navigator.mediaSession!.playbackState = 'playing';
          
          // 重新设置元数据以确保显示
          if (!navigator.mediaSession!.metadata && 'MediaMetadata' in window) {
            navigator.mediaSession!.metadata = new (window as any).MediaMetadata({
              title: title || 'Unknown Title',
              artist: artist || 'Unknown Artist',
              album: album || 'Podcast Episode',
              artwork: artwork || (thumbnailUrl ? [
                {
                  src: thumbnailUrl,
                  sizes: '512x512',
                  type: 'image/png'
                }
              ] : [])
            });
          }
        }
      } catch (error) {
        console.error('Error updating Media Session on play:', error);
      }
    }
    
    // 如果这个播放事件是由seekTo然后立即play引起的，不要触发额外的状态变更
    if (timeSinceSeekRequest < 500) {
      return;
    }
    
    // 🔥 修复循环播放问题：传递实际的播放状态(true)而不是依赖外部isPlaying状态
    if (!externalPlayStateChange.current && onPlayPause) {
      console.log('🎵 VideoPlayer native play event - notifying parent with true');
      onPlayPause(true);
    }
  }, [onPlayPause, title, artist, album, artwork, thumbnailUrl, muted, url, allowCustomMediaSessionActions, setPositionStateSafe]);

  const handleNativePause = useCallback(() => {
    if (isSeeking) {
      console.log('⏸️ VideoPlayer native pause ignored during seek');
      return;
    }
    // 🔥 修复循环播放问题：传递实际的播放状态(false)而不是依赖外部isPlaying状态
    if (!externalPlayStateChange.current && onPlayPause) {
      console.log('⏸️ VideoPlayer native pause event - notifying parent with false');
      onPlayPause(false);
    }
  }, [onPlayPause, isSeeking]);

  const handleVideoTapToggle = useCallback(() => {
    if (!enableTapToggle) {
      return;
    }

    const video = videoRef.current;
    if (!video) {
      return;
    }

    if (video.paused) {
      const playPromise = video.play();
      if (playPromise) {
        playPromise.catch((error) => {
          console.error('Video tap-to-play failed:', error);
        });
      }
    } else {
      video.pause();
    }
  }, [enableTapToggle]);

  // 检查是否为YouTube URL
  const isYouTubeUrl = url && (url.includes('youtube.com') || url.includes('youtu.be'));

  useEffect(() => {
    const video = videoRef.current;
    if (!video || isYouTubeUrl) {
      return;
    }

    mediaCueRegistry.setActiveMediaElement(video, 'video');

    return () => {
      mediaCueRegistry.clearActiveMediaElement(video);
    };
  }, [isYouTubeUrl]);
  
  // 🔥 CORS修复：对非YouTube URL使用代理清理
  const cleanedAudioUrl = React.useMemo(() => {
    if (!url || isYouTubeUrl) {
      return undefined;
    }
    
    const cleaned = cleanAudioUrl(url);
    if (cleaned !== url) {
      console.log('🚀 VideoPlayer: Proxying audio URL from', extractDomain(url), 'via audio-proxy');
    }
    return cleaned;
  }, [url, isYouTubeUrl]);
  
  const computedAspectRatio =
    typeof aspectRatio === "number" && Number.isFinite(aspectRatio) && aspectRatio > 0
      ? aspectRatio
      : 16 / 9;

  const containerClassName = `${
    maintainAspectRatio ? 'relative w-full' : 'relative flex items-center justify-center h-full'
  } bg-black ${className || ''}`;

  const containerStyle = maintainAspectRatio
    ? ({ aspectRatio: `${computedAspectRatio}` } as React.CSSProperties)
    : undefined;

  const videoClassName = maintainAspectRatio
    ? `absolute inset-0 w-full h-full object-center ${
        isPortrait ? 'object-contain' : 'object-cover'
      }`
    : 'relative block w-auto h-auto max-w-full max-h-full object-contain';

  const videoStyle = maintainAspectRatio
    ? undefined
    : ({ maxWidth: '100vw', maxHeight: '100vh', width: 'auto', height: 'auto' } as React.CSSProperties);

  return (
    <div className={containerClassName} style={containerStyle}>
      <video
        ref={videoRef}
        src={cleanedAudioUrl} // 使用清理后的URL避免CORS错误
        className={videoClassName}
        style={videoStyle}
        onTimeUpdate={handleTimeUpdate}
        onDurationChange={handleDurationChange}
        onLoadedMetadata={handleLoadedMetadata}
        onCanPlayThrough={() => {
          if (videoRef.current && currentTime !== undefined && currentTime > 0 && !hasAppliedInitialTime.current) {
            videoRef.current.currentTime = currentTime;
            hasAppliedInitialTime.current = true;
            if (onTimeUpdate) {
              onTimeUpdate(currentTime);
            }
          }
        }}
        onPlay={handleNativePlay}
        onPause={handleNativePause}
        controlsList="nodownload nofullscreen noremoteplayback"
        disablePictureInPicture
        disableRemotePlayback
        playsInline
        webkit-playsinline="true"
        data-playsinline="true"
        crossOrigin="anonymous"
        preload="metadata"
        muted={muted} // 根据props控制是否静音
        onClick={enableTapToggle ? handleVideoTapToggle : undefined}
      />
      
      {/* 显示缩略图直到视频开始播放 */}
      {thumbnailUrl && !hasStartedPlaying && (
        <div 
          className="absolute inset-0 bg-background cursor-pointer flex items-center justify-center"
          onClick={handleThumbnailClick}
        >
          <div className="w-full h-full relative">
            <Image
              src={thumbnailUrl}
              alt="Video thumbnail"
              fill
              className="object-cover rounded-none"
              priority
            />
            {/* 播放按钮覆盖 */}
            {/* <div className="absolute inset-0 flex items-center justify-center">
              <div className="rounded-full bg-black/50 pl-1 w-16 h-16 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="white">
                  <polygon points="5,3 19,12 5,21" />
                </svg>
              </div>
            </div> */}
          </div>
        </div>
      )}
      
      {/* 视频加载中的状态 */}
      {!isVideoLoaded && !thumbnailUrl && (
        <div className="absolute inset-0 bg-black flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
        </div>
      )}
    </div>
  );
} 
